import { ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import {
  blockKey,
  blockUnits,
  stillMissing,
  type CaptureBlock,
  type CaptureText,
} from './tracking-capture';

/**
 * OQ-05-17's sub-table, drawn — "a sub-table that expands under the kit line,
 * one block per tracked component", chosen over a dialog because "a dialog
 * hides how many components still need picking".
 *
 * So the count is the point of this component, not decoration: every block
 * says how many identifiers it is still short of, in the row, while the line
 * above it is still being typed. A clerk who can see "2 still to pick" never
 * reaches Post and reads ERR-MD-03-16 instead.
 *
 * It renders as EXTRA ROWS of the same grid rather than as a panel beside it,
 * which is what keeps a kit's parts attached to their line when the grid
 * scrolls, and what makes the whole thing mirror correctly in Arabic without
 * a single directional class: the cell is indented with `ps-*`, so the
 * indent moves to the right-hand side in RTL exactly as the eye expects.
 */
export function TrackingRows({
  blocks,
  text,
  onChange,
  disabled,
  columns,
  line,
}: {
  blocks: readonly CaptureBlock[];
  text: CaptureText;
  onChange: (key: string, value: string) => void;
  disabled: boolean;
  /** How many columns of the parent grid the sub-row spans. */
  columns: number;
  /** The line number, for the labels a screen reader reads. */
  line: number;
}) {
  const { t } = useTranslation();
  if (blocks.length === 0) return null;

  return (
    <>
      {blocks.map((block) => {
        const key = blockKey(block);
        const typed = text[key] ?? '';
        const missing = stillMissing(block, typed);
        const label =
          block.componentItemId === null
            ? t('tracking.forItem', { name: block.name })
            : t('tracking.forComponent', { name: block.name });
        return (
          <TableRow key={key} className="border-t-0 bg-muted/20">
            <TableCell />
            <TableCell colSpan={Math.max(1, columns - 1)} className="py-1.5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ps-6">
                <ScanLine className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="text-sm font-medium">{label}</span>
                <span className="text-xs text-muted-foreground">
                  {block.kind === 'serial'
                    ? t('tracking.needSerials', { count: block.needed ?? 0 })
                    : t('tracking.needBatch', { qty: blockUnits(block) })}
                </span>
                <input
                  aria-label={
                    block.kind === 'serial'
                      ? t('tracking.serialsFor', { name: block.name, line })
                      : t('tracking.batchFor', { name: block.name, line })
                  }
                  dir="ltr"
                  className={cn(
                    'h-9 min-w-[16rem] flex-1 rounded-md border border-input bg-background px-2 font-mono text-sm outline-none transition-colors',
                    'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
                    'disabled:cursor-not-allowed disabled:opacity-70',
                    missing > 0 && !disabled && 'border-amber-400 dark:border-amber-600',
                  )}
                  placeholder={
                    block.kind === 'serial' ? t('tracking.serialsPh') : t('tracking.batchPh')
                  }
                  value={typed}
                  onChange={(event) => onChange(key, event.target.value)}
                  disabled={disabled}
                />
                {/* The sentence OQ-05-17 rejected a dialog to keep visible. */}
                <span
                  className={cn(
                    'text-xs font-medium',
                    missing > 0
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-emerald-700 dark:text-emerald-400',
                  )}
                >
                  {missing > 0
                    ? t('tracking.stillToPick', { count: missing })
                    : t('tracking.allPicked')}
                </span>
              </div>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}
